// src/components/PrivateRoute.tsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import AccessDenied from "../pages/AccessDenied";
import Permission from "../models/Permission";

interface JwtPayload {
  exp: number; // expiry timestamp (seconds)
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Public routes that don’t require authentication
const publicRoutes = [
  "/login",
  "/forgot-password",
  "/forgot-password/success",
  "/reset-password",
  "/reset-password/invalid",
  "/reset-password/expired",
  "/staffs/update"
];


interface CustomJwtPayload {
  exp?: number;
  userId?: string;
}


const isTokenValid = (authToken: string | null): boolean => {
  if (!authToken) return false;
  try {
    const decoded = jwtDecode<CustomJwtPayload>(authToken);
    if (!decoded?.exp) return false;

    // Protect if from guest portal
    if (!decoded.userId) return false;

    const now = Math.floor(Date.now() / 1000);
    return decoded.exp > now; // token still valid
  } catch (err) {
    console.error("Invalid token:", err);
    return false;
  }
};



export function hasViewPermission(
  permissions: Permission[],
  permission: string
): boolean {

  const commonViewPermission = [
    { name: "test", type: "view", value: true },
    { name: "profile", type: "view", value: true },
    { name: "attendance", type: "view", value: true },
      { name: "expenses", type: "view", value: true },
    { name: "project-reports", type: "view", value: true }
  ];

  const menuViews = ([
    ...permissions, ...commonViewPermission
  ]).filter(
    (perm: any) =>
      perm.type === "view" && perm.value === true
  )
    .map((perm: any) => perm.name.toLowerCase());

  const expandedViews = new Set(menuViews);

  // Grouped permissions
  if (expandedViews.has("logs")) {
    expandedViews.add("attendance-logs");
    // expandedViews.add("attendance");
    expandedViews.add("contacts");
  }

  if (expandedViews.has("staff")) {
    expandedViews.add("staffs");
  }

  // if (expandedViews.has("project reports")) {
  //   expandedViews.add("project-reports");
  // }
  return expandedViews.has(permission.toLowerCase());
}


export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const authToken = localStorage.getItem("authToken");
  const valid = isTokenValid(authToken);
  const location = useLocation();

  // Allow access to public routes
  if (publicRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }


  const path = location.pathname;
  // Redirect unauthenticated users to login with redirect memory
  if (!valid) {
    return <Navigate to={`/login?redirect=${path}`} replace />;
  }

  // Get path to access
  const pathList = path.split("/");
  const firstPath = pathList[1];

  // Get user permission list.
  const userStr = localStorage.getItem("user");

  if (!userStr) return null
  const user = JSON.parse(userStr);


  const hasAccess = hasViewPermission(
    user?.permissions ?? [],
    firstPath
  );

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};
